import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Font, Modal } from '@pepsico-ds/ui'

const UnsavedGuardContext = createContext({
  startEditing: () => {},
  stopEditing: () => {},
  confirmOrProceed: (_action) => {},
  suppressGuard: () => {},
  markEditedIfChanged: (_originalValue, _newValue) => {},
})

export function UnsavedGuardProvider({ children }) {
  const [isEditing, setIsEditing] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const isSuppressedRef = useRef(false)
  // Use ref to always get the latest isEditing value (fixes timing issue)
  const isEditingRef = useRef(false)

  const startEditing = useCallback(() => {
    setIsEditing(true)
    isEditingRef.current = true
  }, [])
  const stopEditing = useCallback(() => {
    setIsEditing(false)
    isEditingRef.current = false
  }, [])
  const suppressGuard = useCallback(() => {
    isSuppressedRef.current = true
  }, [])

  // Wrapper function: Only mark as editing if value actually changed
  const markEditedIfChanged = useCallback((originalValue, newValue) => {
    if (originalValue !== newValue) {
      setIsEditing(true)
    }
  }, [])

  const confirmOrProceed = useCallback(
    (action) => {
      // Use ref to get the latest value (fixes timing issue on first click)
      if (!isEditingRef.current || typeof action !== 'function') {
        action?.()
        return
      }
      setPendingAction(() => action)
      setShowModal(true)
    },
    [] // No dependency on isEditing - use ref instead
  )

  // Native prompt on reload/close
  useEffect(() => {
    const beforeUnload = (e) => {
      // Skip prompt if a forced/logout navigation is underway
      if (isSuppressedRef.current) return

      if (isEditing) {
        e.preventDefault()
        // eslint-disable-next-line no-param-reassign
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [isEditing])

  const onStay = () => {
    setShowModal(false)
    setPendingAction(null)
  }
  const onDiscardAndLeave = () => {
    setShowModal(false)
    setIsEditing(false)
    isEditingRef.current = false
    const action = pendingAction
    setPendingAction(null)
    action?.()
  }

  const value = useMemo(
    () => ({ startEditing, stopEditing, confirmOrProceed, suppressGuard, markEditedIfChanged }),
    [startEditing, stopEditing, confirmOrProceed, suppressGuard, markEditedIfChanged]
  )

  return (
    <UnsavedGuardContext.Provider value={value}>
      {children}
      <Modal
        showModal={showModal}
        onCloseModal={onStay}
        title="Unsaved Progress"
        type="fluid"
        showAuxiliaryButton={false}
        showLink={false}
        showPrimaryButton
        showSecondaryButton
        primaryButtonProps={{
          onClick: () => onDiscardAndLeave(),
          size: 'medium',
          text: 'Leave',
          variant: 'danger',
        }}
        secondaryButtonProps={{
          onClick: () => onStay(),
          size: 'medium',
          text: 'Back',
          variant: 'secondary',
        }}
        showTertiaryButton={false}
      >
        <Font variant="regular">
          If you leave now, your changes won&apos;t be saved. Do you want to continue?
        </Font>
      </Modal>
    </UnsavedGuardContext.Provider>
  )
}

export function useUnsavedGuard() {
  return useContext(UnsavedGuardContext)
}
